import { IconNames } from "@kamae-apps/shared/Components/Fondations/Icons/IconNames.type"
import { useMemo } from "react"
import { SidebarMode, useSidebarMode } from "../../../Contexts/SidebarMode/SidebarModeContext"
import { useUser } from "../../../Contexts/User/UserContext"

export type MenuEntryType = {
  path: string
  iconName: IconNames
  displayNameKey: string
  isAdminRequired: boolean
  isTeamLeaderRequired: boolean
  isSupervisorRequired: boolean
  isDisabled?: boolean
  tooltip?: string
}

export const useMenuEntry = (isAdmin: boolean, isTeamLeader: boolean, isSupervisor: boolean) => {
  const { mode } = useSidebarMode()
  const isMobile = mode === SidebarMode.MOBILE
  const { user } = useUser()

  const displayRankings = !user.company.hide_rankings

  const allMenuEntries = useMemo<MenuEntryType[]>(
    () =>
      [
        {
          path: "/home",
          iconName: "home",
          displayNameKey: "app:sidebar.home.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
          isSupervisorRequired: false,
        },
        {
          path: "/tatami#trainings",
          iconName: "tatami",
          displayNameKey: "app:sidebar.tatami.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
          isSupervisorRequired: false,
        },
        displayRankings && {
          path: "/dojo#users",
          iconName: "kamaeBeltOutlined",
          displayNameKey: "app:sidebar.dojo.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
          isSupervisorRequired: false,
        },
        {
          path: "/phishing",
          iconName: "phishing",
          displayNameKey: "app:sidebar.phishing.label",
          isAdminRequired: !user.company.phishing_enabled,
          isTeamLeaderRequired: false,
          isSupervisorRequired: false,
          isDisabled: !user.company.phishing_enabled,
          tooltip: "app:sidebar.phishing.disabled",
        },
        {
          path: "/reflexes",
          iconName: "book",
          displayNameKey: "app:sidebar.reflexe.label",
          isAdminRequired: false,
          isTeamLeaderRequired: false,
          isSupervisorRequired: false,
        },
        {
          path: "/dashboard",
          iconName: "stat",
          displayNameKey: "app:sidebar.dashboard.label",
          isAdminRequired: true,
          isTeamLeaderRequired: true,
          isSupervisorRequired: true,
        },
        {
          path: "/administration",
          iconName: "settings",
          displayNameKey: "administration:sidebar.label",
          isAdminRequired: true,
          isTeamLeaderRequired: false,
          isSupervisorRequired: false,
        },
      ].filter(Boolean) as MenuEntryType[],
    [user, displayRankings]
  )

  const menuEntries = useMemo(() => {
    return isMobile
      ? allMenuEntries.filter(entry => !["/administration", "/dashboard"].includes(entry.path))
      : allMenuEntries
  }, [isMobile, allMenuEntries])

  const filteredMenuEntries = useMemo(() => {
    return menuEntries.filter(
      menuEntry =>
        (menuEntry.isAdminRequired && isAdmin) ||
        (menuEntry.isTeamLeaderRequired && isTeamLeader) ||
        (menuEntry.isSupervisorRequired && isSupervisor) ||
        (!menuEntry.isAdminRequired && !menuEntry.isTeamLeaderRequired)
    )
  }, [menuEntries, isAdmin, isTeamLeader, isSupervisor])

  return { filteredMenuEntries }
}
